import { useEffect } from "react";

import { LoadingIndicator } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useCreateCandidacyOwner } from "@/hooks/mutations/useCreateCandidacyOwner";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { ShortlistTakeover } from "@/types";

// Shown when shortlisting a candidacy owned by another staffer.
export const ShortlistTakeoverDialog = ({
  candidacyId,
  ownerUserId,
  onClose,
  onShortlist,
}: ShortlistTakeover & { onClose: () => void }) => {
  /* Database CRUD Operations */
  const { mutate: createCandidacyOwner, isPending } = useCreateCandidacyOwner();

  /* Context */
  const currentUser = useGetCurrentUser();
  const { userNameMap } = useTaggableUsers();

  /* Derived */
  const ownerName = userNameMap[ownerUserId] || "another staffer";

  /* Handlers */
  const handleTakeOwnership = () => {
    if (currentUser == null) return;
    createCandidacyOwner(
      {
        candidacyId,
        createCandidacyOwnerRequest: {
          currentOwner: ownerUserId,
          newOwner: currentUser.id,
        },
      },
      {
        onSuccess: () => {
          onClose();
          onShortlist();
        },
      },
    );
  };

  const handleKeepOwner = () => {
    onClose();
    onShortlist();
  };

  /* React Hooks */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isPending) return;
      const key = event.key.toLowerCase();
      if (key === "y") handleTakeOwnership();
      if (key === "n") handleKeepOwner();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  /* Render */
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex flex-col gap-10">
        <DialogHeader className="flex flex-col gap-4">
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Take ownership?
          </DialogTitle>
          <DialogDescription className="!m-0 whitespace-pre-line text-base font-medium text-slate-700">
            This candidacy is owned by {ownerName}. Take ownership, or keep{" "}
            {ownerName} as the owner. If the scheduling agent is on, it kicks
            off for whoever owns it.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            className="rounded-full"
            disabled={isPending}
            onClick={handleKeepOwner}
            variant="secondary"
          >
            Keep {ownerName} (N)
          </Button>
          <Button
            className="rounded-full"
            disabled={isPending}
            onClick={handleTakeOwnership}
          >
            {isPending ? (
              <LoadingIndicator gearColor="white" variant="small" />
            ) : (
              <span>Take ownership (Y)</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
